/**
 * 设备设置：选摄像头与麦克风，并当场验一验。
 *
 * 「能选」还不够 —— 选错设备的表现和设备坏掉一模一样（没画面、没声音）。
 * 所以这里给出画面预览和音量条：选完立刻能看到自己、看到说话时条会动，
 * 上课前就能确认，而不是上课中途才发现。
 */

import { useEffect, useRef, useState } from 'react'

import { applySpeaker, canPickSpeaker, getPreferred, listDevices, setPreferred, type DeviceKind, type DeviceList } from '../lib/devices'
import { virtualBgSupported, type BgMode } from '../lib/virtualBgSupport'
import { useRoomStore } from '../stores/roomStore'
import { mediaBlockedReason } from '../lib/media'

export function DeviceSettings({ onClose }: { onClose: () => void }) {
  const [list, setList] = useState<DeviceList>({ cameras: [], mics: [], speakers: [] })
  const [sel, setSel] = useState(getPreferred())
  const [err, setErr] = useState(mediaBlockedReason() ?? '')
  const [level, setLevel] = useState(0)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    if (err) return
    let alive = true
    listDevices()
      .then((d) => alive && setList(d))
      .catch(() => alive && setErr('无法列出设备，请检查浏览器权限'))
    return () => {
      alive = false
    }
  }, [err])

  // 预览流：每次换设备都重开一路，退出时务必关掉 ——
  // 忘了关的话摄像头指示灯会一直亮着，用户会以为在被偷拍。
  useEffect(() => {
    if (err) return
    let alive = true
    let ctx: AudioContext | null = null
    let raf = 0

    ;(async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: sel.camera ? { deviceId: { ideal: sel.camera } } : true,
          audio: sel.mic ? { deviceId: { ideal: sel.mic } } : true,
        })
        if (!alive) {
          s.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = s
        if (videoRef.current) {
          videoRef.current.srcObject = s
          videoRef.current.play().catch(() => {})
        }
        const Ctor = window.AudioContext || (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        ctx = new Ctor()
        const an = ctx.createAnalyser()
        an.fftSize = 512
        ctx.createMediaStreamSource(s).connect(an)
        const buf = new Uint8Array(an.frequencyBinCount)
        const tick = () => {
          an.getByteFrequencyData(buf)
          let peak = 0
          for (const v of buf) if (v > peak) peak = v
          setLevel(peak)
          raf = requestAnimationFrame(tick)
        }
        tick()
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : '打不开所选设备')
      }
    })()

    return () => {
      alive = false
      cancelAnimationFrame(raf)
      void ctx?.close()
      streamRef.current?.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
  }, [sel.camera, sel.mic, err])

  const setSpeakerId = useRoomStore((s) => s.setSpeakerId)
  const media = useRoomStore((s) => s.media)
  const [bgMode, setBgMode] = useState<BgMode>(() => media?.backgroundMode() ?? 'off')
  const [bgBusy, setBgBusy] = useState(false)
  const bgOk = virtualBgSupported()

  // 切背景要重新采流并重新协商，几百毫秒；期间禁用避免连点。
  const chooseBg = async (m: BgMode) => {
    if (!media || bgBusy) return
    setBgBusy(true)
    setBgMode(m)
    try {
      await media.setBackground(m)
    } catch {
      setBgMode('off')
    } finally {
      setBgBusy(false)
    }
  }
  const [testing, setTesting] = useState(false)
  const choose = (kind: DeviceKind, id: string) => {
    setPreferred(kind, id)
    setSel((s) => ({ ...s, [kind]: id }))
    if (kind === 'speaker') setSpeakerId(id)
  }

  // 测试扬声器：放 1.2 秒提示音。走 <audio> 而不是直接 AudioContext 出声，
  // 因为只有媒体元素能 setSinkId —— 否则测的永远是系统默认输出。
  const testSpeaker = async () => {
    if (testing) return
    setTesting(true)
    try {
      const Ctor = window.AudioContext || (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const ctx = new Ctor()
      const dest = ctx.createMediaStreamDestination()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      gain.gain.value = 0.25
      osc.type = 'sine'
      osc.frequency.value = 660
      osc.connect(gain).connect(dest)
      const el = new window.Audio()
      el.srcObject = dest.stream
      await applySpeaker(el)
      osc.start()
      await el.play().catch(() => {})
      await new Promise((r) => setTimeout(r, 1200))
      osc.stop()
      el.pause()
      await ctx.close()
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="dlg-mask" onClick={onClose}>
      <div className="dlg narrow dev-dlg" onClick={(e) => e.stopPropagation()}>
        <h3>音视频设置</h3>
        {err ? (
          <p className="dev-err">{err}</p>
        ) : (
          <>
            <label>
              摄像头
              <select value={sel.camera} onChange={(e) => choose('camera', e.target.value)}>
                <option value="">跟随系统默认</option>
                {list.cameras.map((d) => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
            </label>
            {/* 预览要镜像，跟照镜子一致 */}
            <video ref={videoRef} className="dev-preview" autoPlay playsInline muted />

            <label>
              麦克风
              <select value={sel.mic} onChange={(e) => choose('mic', e.target.value)}>
                <option value="">跟随系统默认</option>
                {list.mics.map((d) => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
            </label>
            <div className="dev-meter" title="对着麦克风说话，这条会跟着动">
              <span style={{ width: `${Math.min(100, (level / 180) * 100)}%` }} />
            </div>
            {canPickSpeaker() && (
              <label>
                扬声器
                <span className="dev-row">
                  <select value={sel.speaker} onChange={(e) => choose('speaker', e.target.value)}>
                    <option value="">跟随系统默认</option>
                    {list.speakers.map((d) => (
                      <option key={d.id} value={d.id}>{d.label}</option>
                    ))}
                  </select>
                  <button type="button" onClick={testSpeaker} disabled={testing} className="dev-test">
                    {testing ? '播放中…' : '测试扬声器'}
                  </button>
                </span>
              </label>
            )}
            {bgOk && (
              <label>
                背景
                <span className="dev-row bg-row">
                  <button
                    type="button"
                    className={bgMode === 'off' ? 'on' : ''}
                    disabled={bgBusy}
                    onClick={() => void chooseBg('off')}
                  >
                    无
                  </button>
                  <button
                    type="button"
                    className={bgMode === 'blur' ? 'on' : ''}
                    disabled={bgBusy}
                    onClick={() => void chooseBg('blur')}
                  >
                    虚化背景
                  </button>
                </span>
              </label>
            )}
            {bgOk && bgBusy && <p className="dev-tip">正在切换背景…首次开启要加载分割模型，请稍等</p>}
            <p className="dev-tip">说句话看看音量条会不会动；这里选的设备下次上课仍然有效。</p>
          </>
        )}
        <div className="dlg-foot">
          <button className="primary" onClick={onClose}>完成</button>
        </div>
      </div>
    </div>
  )
}
